/**
 * The Documents tab's PDF viewer: as tall as the screen with its own scroll, pages fitted to the width (zoom from
 * there), an "expand" that fills the whole window, and annotations shared with your team (annotationsStore.ts,
 * worker/src/annotations.js):
 *   Select     click a highlight or drawing to open its thread; notes are pins on the page
 *   Highlight  select text on a page: it is highlighted, with an optional note
 *   Note       click anywhere on a page to pin a note there
 *   Draw       draw freehand strokes on a page, then save them (with an optional note)
 * Each annotation has a comment thread in the side panel. Pages are drawn with pdf.js as they near the view, with
 * pdf.js's text layer on top so text can be selected. Positions are stored as fractions of the page.
 *
 * On the public meeting page without dashboard access, choosing a tool asks you to sign in (app/viewer.ts).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import {
  Download, ExternalLink, Highlighter, Maximize2, MessageSquare, Minimize2, MousePointer2, PanelRight, Pencil, StickyNote, Trash2, ZoomIn, ZoomOut,
} from "lucide-react";
import { getViewer, signInToAct } from "../../app/viewer";
import { site } from "../../app/site";
import {
  annotationsStore, docKeyOf, useAnnotations,
  type Annotation, type AnnotationColor, type AnnotationKind, type NewAnnotation, type Rect,
} from "./annotationsStore";

type PdfDoc = import("pdfjs-dist").PDFDocumentProxy;
type PdfJs = typeof import("pdfjs-dist");
let pdfjs: Promise<PdfJs> | null = null;
function loadPdfjs() {
  pdfjs ??= import("pdfjs-dist").then((m) => {
    m.GlobalWorkerOptions.workerSrc = new URL("./pdf.worker.min.mjs", import.meta.url).href;
    return m;
  });
  return pdfjs;
}

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2];   // × the fitted width
const FIT_PAD = 24;                           // px on each side of the page
const COLORS: AnnotationColor[] = ["yellow", "green", "blue", "red"];
type Tool = "select" | AnnotationKind;
const TOOLS: { id: Tool; label: string; Icon: typeof Highlighter }[] = [
  { id: "select", label: "Select", Icon: MousePointer2 },
  { id: "highlight", label: "Highlight", Icon: Highlighter },
  { id: "note", label: "Note", Icon: StickyNote },
  { id: "ink", label: "Draw", Icon: Pencil },
];
const KIND_LABEL: Record<AnnotationKind, string> = { highlight: "Highlight", note: "Note", ink: "Drawing" };

/** Something being made, not saved yet: shown dashed on its page and in the panel's composer. */
type Draft = NewAnnotation;

const when = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const inkD = (paths: number[][], h: number) =>
  paths.map((p) => p.reduce((d, v, i) => d + (i % 2 ? `${(v * h).toFixed(4)} ` : `${i ? "L" : "M"}${v.toFixed(4)} `), "")).join("");

/** The selection's boxes on one page, as fractions of the page, one per line (pdf.js gives one per text run). */
function selectionRects(range: Range, page: HTMLElement): Rect[] {
  const box = page.getBoundingClientRect();
  const raw = [...range.getClientRects()]
    .map((r) => ({ x: (r.left - box.left) / box.width, y: (r.top - box.top) / box.height, w: r.width / box.width, h: r.height / box.height }))
    .filter((r) => r.w > 0.001 && r.h > 0.001 && r.x < 1 && r.y < 1 && r.x + r.w > 0 && r.y + r.h > 0)
    .map((r) => ({ x: Math.max(0, r.x), y: Math.max(0, r.y), w: Math.min(1, r.x + r.w) - Math.max(0, r.x), h: Math.min(1, r.y + r.h) - Math.max(0, r.y) }));
  // Merge boxes on the same line (overlapping vertically, touching horizontally).
  raw.sort((a, b) => a.y - b.y || a.x - b.x);
  const out: Rect[] = [];
  for (const r of raw) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.y - r.y) < last.h * 0.5 && r.x <= last.x + last.w + 0.01) {
      const right = Math.max(last.x + last.w, r.x + r.w), bottom = Math.max(last.y + last.h, r.y + r.h);
      last.y = Math.min(last.y, r.y); last.w = right - last.x; last.h = bottom - last.y;
    } else out.push({ ...r });
  }
  return out;
}

const pointIn = (x: number, y: number, r: Rect) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
function nearInk(x: number, y: number, paths: number[][], ratio: number, tol = 0.012) {
  for (const p of paths) for (let i = 0; i < p.length; i += 2) if (Math.hypot(p[i] - x, (p[i + 1] - y) * ratio) < tol) return true;
  return false;
}

// ---------- one page ----------

interface PageProps {
  doc: PdfDoc; lib: PdfJs; n: number; width: number; root: HTMLElement | null; tool: Tool; color: AnnotationColor;
  annotations: Annotation[]; draft: Draft | null; selected: string | null;
  onVisible: (n: number) => void; onPick: (id: string) => void; onNote: (n: number, x: number, y: number) => void; onStroke: (n: number, path: number[]) => void;
}

function PdfPage({ doc, lib, n, width, root, tool, color, annotations, draft, selected, onVisible, onPick, onNote, onStroke }: PageProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const live = useRef<SVGPathElement>(null);
  const stroke = useRef<number[] | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);   // the page in PDF units
  const [near, setNear] = useState(n === 1);
  const ratio = size ? size.h / size.w : 11 / 8.5;

  useEffect(() => {
    const el = wrap.current!;
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) setNear(true); }), { root, rootMargin: "800px 0px" });
    // "Page X of Y": the page crossing the middle of the viewer.
    const seen = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) onVisible(n); }), { root, rootMargin: "-50% 0px -50% 0px" });
    io.observe(el); seen.observe(el);
    return () => { io.disconnect(); seen.disconnect(); };
  }, [n, onVisible, root]);

  useEffect(() => {
    if (!near) return;
    let render: { cancel(): void; promise: Promise<void> } | null = null, text: { cancel(): void } | null = null, dead = false;
    doc.getPage(n).then((page) => {
      if (dead) return;
      const base = page.getViewport({ scale: 1 });
      setSize({ w: base.width, h: base.height });
      const css = width / base.width;
      const viewport = page.getViewport({ scale: css * devicePixelRatio });
      const canvas = canvasRef.current!;
      canvas.width = Math.floor(viewport.width); canvas.height = Math.floor(viewport.height);
      render = page.render({ canvas, viewport });
      render.promise.catch(() => {});
      // pdf.js's text layer (selectable text), sized by the CSS variables set on the page below.
      const div = textRef.current!;
      div.replaceChildren();
      const layer = new lib.TextLayer({ textContentSource: page.streamTextContent(), container: div, viewport: page.getViewport({ scale: css }) });
      text = layer;
      layer.render().catch(() => {});
    });
    return () => { dead = true; render?.cancel(); text?.cancel(); };
  }, [doc, lib, n, width, near]);

  const h = width * ratio;
  const at = (e: { clientX: number; clientY: number }) => {
    const b = wrap.current!.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - b.left) / b.width)), y: Math.min(1, Math.max(0, (e.clientY - b.top) / b.height)) };
  };
  // Select: a click (not a text selection) on a highlight or a drawing opens its thread.
  const onClick = (e: React.MouseEvent) => {
    if (tool !== "select" || !getSelection()?.isCollapsed) return;
    const p = at(e);
    const hit = [...annotations].reverse().find((a) =>
      (a.kind === "highlight" && "rects" in a.data && a.data.rects.some((r) => pointIn(p.x, p.y, r)))
      || (a.kind === "ink" && "paths" in a.data && nearInk(p.x, p.y, a.data.paths, ratio)));
    if (hit) onPick(hit.id);
  };
  const down = (e: RPointerEvent<SVGSVGElement>) => {
    if (tool === "note") { const p = at(e); onNote(n, p.x, p.y); return; }
    if (tool !== "ink") return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = at(e);
    stroke.current = [p.x, p.y];
  };
  const move = (e: RPointerEvent<SVGSVGElement>) => {
    if (!stroke.current) return;
    const p = at(e);
    stroke.current.push(p.x, p.y);
    live.current?.setAttribute("d", inkD([stroke.current], ratio));   // straight to the DOM: no render per move
  };
  const up = () => {
    const s = stroke.current;
    stroke.current = null;
    live.current?.setAttribute("d", "");
    if (s && s.length >= 4) onStroke(n, s);
  };

  const shapes = (list: Annotation[] | Draft[], drafting: boolean) => list.map((a, i) => {
    const id = "id" in a ? a.id : `draft${i}`;
    const cls = `ann ann--${a.kind}${drafting ? " ann--draft" : ""}${"id" in a && a.id === selected ? " ann--selected" : ""}`;
    if (a.kind === "highlight" && "rects" in a.data) {
      return <g key={id} className={cls} data-color={a.color}>{a.data.rects.map((r, j) => <rect key={j} x={r.x} y={r.y * ratio} width={r.w} height={r.h * ratio} rx={0.002} />)}</g>;
    }
    if (a.kind === "ink" && "paths" in a.data) {
      return <path key={id} className={cls} data-color={a.color} d={inkD(a.data.paths, ratio)} strokeWidth={a.data.width} />;
    }
    return null;
  });
  const pins = [...annotations, ...(draft ? [draft] : [])].filter((a) => a.kind === "note" && "x" in a.data);
  const pageAnns = annotations;
  const pageDraft = draft ? [draft] : [];
  return (
    <div ref={wrap} className="doc-page doc-page--pdf" data-page={n} onClick={onClick}
      style={{ width, height: h, ["--total-scale-factor" as string]: size ? width / size.w : 1, ["--scale-round-x" as string]: "1px", ["--scale-round-y" as string]: "1px" }}>
      <canvas ref={canvasRef} className="doc-canvas" aria-label={`Page ${n}`} />
      {/* Highlights under the text (so the text stays selectable), drawings and pins over it. */}
      <svg className="ann-layer ann-layer--under" viewBox={`0 0 1 ${ratio}`} preserveAspectRatio="none" aria-hidden>
        {shapes(pageAnns.filter((a) => a.kind === "highlight"), false)}
        {shapes(pageDraft.filter((a) => a.kind === "highlight"), true)}
      </svg>
      <div ref={textRef} className="textLayer" data-tool={tool} />
      <svg className="ann-layer ann-layer--over" viewBox={`0 0 1 ${ratio}`} preserveAspectRatio="none" data-tool={tool} aria-hidden={tool === "select" || tool === "highlight"}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        {shapes(pageAnns.filter((a) => a.kind === "ink"), false)}
        {shapes(pageDraft.filter((a) => a.kind === "ink"), true)}
        <path ref={live} className="ann ann--ink ann--draft" data-color={color} d="" strokeWidth={0.003} />
      </svg>
      {pins.map((a, i) => {
        const p = a.data as { x: number; y: number };
        const id = "id" in a ? (a as Annotation).id : null;
        return (
          <button key={id ?? `pin${i}`} type="button" className={`ann-pin${id ? "" : " ann-pin--draft"}${id && id === selected ? " ann-pin--selected" : ""}`} data-color={a.color}
            style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }} aria-label={`Note on page ${n}`} onClick={(e) => { e.stopPropagation(); if (id) onPick(id); }}>
            <StickyNote size={14} />
          </button>
        );
      })}
    </div>
  );
}

// ---------- the side panel: composer, then every annotation with its thread ----------

function Thread({ a, open, onOpen, onJump }: { a: Annotation; open: boolean; onOpen: () => void; onJump: () => void }) {
  const [reply, setReply] = useState("");
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(a.body ?? "");
  const st = annotationsStore.getState();
  return (
    <li className="ann-card" data-open={open || undefined} data-color={a.color}>
      <button type="button" className="ann-card__head" onClick={() => { onOpen(); onJump(); }}>
        <span className="ann-swatch" data-color={a.color} aria-hidden />
        <span className="ann-card__kind">{KIND_LABEL[a.kind]}</span>
        <span className="ann-card__page">Page {a.page}</span>
        <span className="ann-card__by">{a.mine ? "You" : a.author.name} · {when(a.createdAt)}</span>
      </button>
      {a.quote && <blockquote className="ann-card__quote">{a.quote}</blockquote>}
      {editing ? (
        <form className="ann-compose" onSubmit={(e) => { e.preventDefault(); void st.update(a.id, { body: body.trim() || null }); setEditing(false); }}>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} aria-label="Note" autoFocus />
          <span className="ann-compose__actions"><button type="button" className="btn" onClick={() => { setEditing(false); setBody(a.body ?? ""); }}>Cancel</button><button className="btn btn--primary">Save</button></span>
        </form>
      ) : a.body ? <p className="ann-card__body">{a.body}</p> : null}
      {open && (
        <div className="ann-thread">
          {a.comments.length > 0 && (
            <ul className="ann-comments">
              {a.comments.map((c) => (
                <li key={c.id} className="ann-comment">
                  <span className="ann-card__by">{c.mine ? "You" : c.author.name} · {when(c.createdAt)}</span>
                  <p>{c.body}</p>
                  {c.mine && <button type="button" className="link ann-comment__delete" onClick={() => void st.removeComment(a.id, c.id)}>Delete</button>}
                </li>
              ))}
            </ul>
          )}
          <form className="ann-reply" onSubmit={(e) => { e.preventDefault(); const t = reply.trim(); if (t) void st.reply(a.id, t).then((ok) => ok && setReply("")); }}>
            <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply" aria-label="Reply" maxLength={4000} />
            <button className="btn" disabled={!reply.trim()}>Reply</button>
          </form>
          {a.mine && !editing && (
            <span className="ann-card__actions">
              <span className="ann-colors" role="radiogroup" aria-label="Color">
                {COLORS.map((c) => <button key={c} type="button" role="radio" aria-checked={a.color === c} aria-label={c} className="ann-swatch" data-color={c} onClick={() => void st.update(a.id, { color: c })} />)}
              </span>
              <button type="button" className="link" onClick={() => setEditing(true)}>{a.body ? "Edit" : "Add a note"}</button>
              <button type="button" className="icon-btn" aria-label="Delete" onClick={() => void st.remove(a.id)}><Trash2 size={14} /></button>
            </span>
          )}
        </div>
      )}
    </li>
  );
}

function Panel({ draft, onSave, onCancel, onJump, shared }: { draft: Draft | null; onSave: (body: string) => void; onCancel: () => void; onJump: (page: number) => void; shared: boolean }) {
  const items = useAnnotations((s) => s.items);
  const selected = useAnnotations((s) => s.selected);
  const [body, setBody] = useState("");
  useEffect(() => setBody(""), [draft?.kind, draft?.page]);
  useEffect(() => {
    if (selected) document.getElementById(`ann-${selected}`)?.scrollIntoView({ block: "nearest" });
  }, [selected]);
  return (
    <aside className="doc-notes" aria-label="Annotations">
      <div className="doc-notes__head">
        <h3>Annotations</h3>
        <span className="muted small">{shared ? "Shared with your team" : "Only you"}</span>
      </div>
      {draft && (
        <form className="ann-compose ann-compose--new" onSubmit={(e) => { e.preventDefault(); onSave(body.trim()); }}>
          <span className="ann-card__kind">{KIND_LABEL[draft.kind]} · Page {draft.page}</span>
          {draft.quote && <blockquote className="ann-card__quote">{draft.quote}</blockquote>}
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={4000} autoFocus
            placeholder={draft.kind === "note" ? "Write a note" : "Add a note (optional)"} aria-label="Note" />
          <span className="ann-compose__actions">
            <button type="button" className="btn" onClick={onCancel}>Cancel</button>
            <button className="btn btn--primary" disabled={draft.kind === "note" && !body.trim()}>Save</button>
          </span>
        </form>
      )}
      {items.length === 0 && !draft ? (
        <p className="muted small doc-notes__empty">No annotations yet. Highlight text, add a note or draw on a page.</p>
      ) : (
        <ul className="ann-list">
          {items.map((a) => (
            <div key={a.id} id={`ann-${a.id}`}>
              <Thread a={a} open={selected === a.id} onOpen={() => annotationsStore.getState().select(selected === a.id ? null : a.id)} onJump={() => onJump(a.page)} />
            </div>
          ))}
        </ul>
      )}
    </aside>
  );
}

// ---------- the viewer ----------

export function PdfViewer({ url, fileName, shared }: { url: string; fileName: string; shared: boolean }) {
  const viewer = getViewer();
  const [lib, setLib] = useState<PdfJs | null>(null);
  const [doc, setDoc] = useState<PdfDoc | null>(null);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(2);          // index into ZOOMS (fit to width)
  const [fit, setFit] = useState(0);            // the fitted page width, px
  const [tool, setTool] = useState<Tool>("select");
  const [color, setColor] = useState<AnnotationColor>("yellow");
  const [expanded, setExpanded] = useState(false);
  const [panel, setPanel] = useState(false);   // opens with a tool, an annotation picked, or a document that has some
  const [draft, setDraft] = useState<Draft | null>(null);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const items = useAnnotations((s) => s.items);
  const selected = useAnnotations((s) => s.selected);
  const key = useMemo(() => docKeyOf(url), [url]);

  // The document and its annotations.
  useEffect(() => {
    let dead = false, task: import("pdfjs-dist").PDFDocumentLoadingTask | null = null;
    setDoc(null); setFailed(false); setPage(1); setDraft(null);
    loadPdfjs().then((m) => {
      if (dead) return;
      setLib(m);
      task = m.getDocument({ url });
      return task.promise.then((d) => { if (!dead) setDoc(d); });
    }).catch(() => !dead && setFailed(true));
    return () => { dead = true; task?.destroy(); };
  }, [url]);
  useEffect(() => {
    if (!viewer.admitted) return;
    void annotationsStore.getState().load(key);
    const again = (e: Event) => { if ((e as CustomEvent<{ doc?: string }>).detail?.doc === key) void annotationsStore.getState().load(key); };
    addEventListener("team:annotations", again);
    return () => removeEventListener("team:annotations", again);
  }, [key, viewer.admitted]);

  // A document that already has annotations opens with the panel (once per document).
  const opened = useRef<string | null>(null);
  useEffect(() => { if (items.length && opened.current !== key) { opened.current = key; setPanel(true); } }, [items.length, key]);

  // Fit the page to the viewer's width (and refit when it changes: panel, window, expand).
  useEffect(() => {
    if (!scroller) return;
    const ro = new ResizeObserver(() => setFit(Math.max(240, Math.min(1400, scroller.clientWidth - FIT_PAD * 2))));
    ro.observe(scroller);
    return () => ro.disconnect();
  }, [scroller]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (draft) setDraft(null);
      else if (expanded) setExpanded(false);
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [draft, expanded]);

  const choose = (t: Tool) => {
    if (t !== "select" && !viewer.admitted) return signInToAct();
    setTool(t); setPanel(true);
    if (t !== "ink" && draft?.kind === "ink") void save("");
  };
  const jump = useCallback((n: number) => {
    scroller?.querySelector<HTMLElement>(`[data-page="${n}"]`)?.scrollIntoView({ block: "start" });
  }, [scroller]);

  async function save(body: string) {
    if (!draft) return;
    const d = draft;
    setDraft(null);
    await annotationsStore.getState().add({ ...d, body: body || null });
  }

  // Highlight: a text selection inside one page becomes a draft highlight.
  const onMouseUp = () => {
    if (tool !== "highlight") return;
    const sel = getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const pageEl = (range.startContainer.parentElement ?? null)?.closest<HTMLElement>("[data-page]");
    if (!pageEl) return;
    const rects = selectionRects(range, pageEl);
    const quote = sel.toString().replace(/\s+/g, " ").trim();
    sel.removeAllRanges();
    if (!rects.length || !quote) return;
    setDraft({ kind: "highlight", page: Number(pageEl.dataset.page), color, data: { rects }, quote: quote.slice(0, 2000) });
  };
  const onNote = useCallback((n: number, x: number, y: number) => setDraft({ kind: "note", page: n, color, data: { x, y } }), [color]);
  const onStroke = useCallback((n: number, path: number[]) => {
    setDraft((d) => {
      if (d?.kind === "ink" && d.page === n && "paths" in d.data) return { ...d, data: { ...d.data, paths: [...d.data.paths, path] } };
      // A stroke on another page: the drawing so far is saved first.
      if (d?.kind === "ink") void annotationsStore.getState().add({ ...d, body: null });
      return { kind: "ink", page: n, color, data: { paths: [path], width: 0.003 } };
    });
  }, [color]);
  const pick = useCallback((id: string) => { annotationsStore.getState().select(id); setPanel(true); }, []);

  const width = fit * ZOOMS[zoom];
  const byPage = useMemo(() => {
    const m = new Map<number, Annotation[]>();
    for (const a of items) m.set(a.page, [...(m.get(a.page) ?? []), a]);
    return m;
  }, [items]);
  const showPanel = panel && viewer.admitted;
  const count = items.length;

  return (
    <section className="doc-preview doc-viewer" data-expanded={expanded || undefined} aria-label={`Preview of ${fileName}`}>
      <div className="doc-toolbar">
        <span className="doc-name-group">
          <span className="doc-name">{fileName}</span>
          {doc && <span className="doc-page-count">Page {page} of {doc.numPages}</span>}
        </span>
        <span className="doc-tools">
          {/* Annotating needs an account; a site without accounts (another city's) shows the document alone. */}
          {site.accounts && <>
            <span className="doc-annotate" role="radiogroup" aria-label="Tool">
              {TOOLS.map(({ id, label, Icon }) => (
                <button key={id} type="button" role="radio" aria-checked={tool === id} className="icon-btn" aria-label={label} title={label} onClick={() => choose(id)}><Icon size={15} /></button>
              ))}
            </span>
            {tool !== "select" && (
              <span className="ann-colors" role="radiogroup" aria-label="Color">
                {COLORS.map((c) => <button key={c} type="button" role="radio" aria-checked={color === c} aria-label={c} className="ann-swatch" data-color={c} onClick={() => setColor(c)} />)}
              </span>
            )}
            <span className="doc-tools__sep" aria-hidden />
          </>}
          <button className="icon-btn" aria-label="Zoom out" disabled={zoom === 0} onClick={() => setZoom(zoom - 1)}><ZoomOut size={15} /></button>
          <button className="icon-btn" aria-label="Zoom in" disabled={zoom === ZOOMS.length - 1} onClick={() => setZoom(zoom + 1)}><ZoomIn size={15} /></button>
          <button className="icon-btn" aria-label={expanded ? "Exit full screen" : "Full screen"} aria-pressed={expanded} onClick={() => setExpanded(!expanded)}>
            {expanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
          {viewer.admitted && (
            <button className="icon-btn doc-notes-toggle" aria-label="Annotations" aria-pressed={panel} onClick={() => setPanel(!panel)}>
              {count > 0 ? <MessageSquare size={15} /> : <PanelRight size={15} />}{count > 0 && <span className="count-badge">{count}</span>}
            </button>
          )}
          <a className="icon-btn" href={url} target="_blank" rel="noreferrer" aria-label="Open in a new tab"><ExternalLink size={15} /></a>
          <a className="btn btn--primary doc-download" href={url} download={fileName}><Download size={14} />Download</a>
        </span>
      </div>
      <div className="doc-body">
        <div ref={setScroller} className="doc-pages" tabIndex={0} role="region" aria-label={`${fileName}, pages`} data-tool={tool} onMouseUp={onMouseUp}>
          {failed ? <p className="doc-error">This file can't be previewed. Download it to read it.</p>
            : !doc || !lib || !fit ? <div className="doc-page doc-page--loading" style={{ width: width || 500, height: (width || 500) * 11 / 8.5 }} />
            : Array.from({ length: doc.numPages }, (_, i) => (
              <PdfPage key={i} doc={doc} lib={lib} n={i + 1} width={width} root={scroller} tool={tool} color={color}
                annotations={byPage.get(i + 1) ?? []} draft={draft?.page === i + 1 ? draft : null} selected={selected}
                onVisible={setPage} onPick={pick} onNote={onNote} onStroke={onStroke} />
            ))}
        </div>
        {showPanel && <Panel draft={draft} onSave={(b) => void save(b)} onCancel={() => setDraft(null)} onJump={jump} shared={shared} />}
      </div>
    </section>
  );
}
